import { useState } from 'react'
import type { JSX } from 'react'
import { Link } from 'react-router'
import { api, call } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { useBrand } from '@/brand/context'
import { SiteChip } from '@/settings/SiteChip'
import { describeCadence, scheduleBody } from '@/settings/run-schedule/cadence'
import type { Schedule } from '@/settings/run-schedule/cadence'
import { RunScheduleForm } from '@/settings/run-schedule/form'
import { summariseSchedule } from '@/settings/run-schedule/summary'
import { useSettingsSite } from '@/settings/site'
import { may, useActiveTenant } from '@/tenant/context'
import { SettingRow } from '@/ui/SettingRow'
import { useOneEditing } from '@/ui/useOneEditing'
import { Empty, ErrorState, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'

/**
 * Settings › SEO › Run schedule — per site, hence the chip.
 *
 * Settings consolidation (2026-09-15). The schedule was edited on
 * `/seo/triggers`, which keeps *By hand* and a read-only summary. The form is
 * `settings/run-schedule/form.tsx`, shared with the wizard.
 */
export function RunSchedulePanel(): JSX.Element {
  const brand = useBrand()
  const { tenant, membership } = useActiveTenant()
  const editable = may(membership, 'tenant:update')
  const site = useSettingsSite()
  const one = useOneEditing()
  const toast = useToast()
  const [savedAt, setSavedAt] = useState<string | null>(null)
  const siteId = site.site?.id ?? null

  const schedule = useQuery(
    (signal) =>
      siteId === null
        ? Promise.resolve({ ok: true as const, data: null })
        : call(() =>
            api.GET('/api/v1/seo/run-schedules/{site_id}', {
              params: { path: { site_id: siteId } },
              signal,
            }),
          ),
    [siteId],
  )
  const save = useMutation((body: Schedule) =>
    call(() =>
      api.PUT('/api/v1/seo/run-schedules/{site_id}', {
        params: { path: { site_id: body.site_id } },
        body: scheduleBody(body),
      }),
    ),
  )

  return (
    <div className="ar-stack">
      <div className="ar-row">
        <h2 className="ar-h2">Run schedule</h2>
        <span className="ar-card__spacer" />
        <SiteChip site={site} />
      </div>

      {site.status === 'loading' || (siteId !== null && schedule.state.status === 'loading') ? (
        <Loading label="Loading the schedule" lines={2} />
      ) : null}
      {site.status === 'error' && site.failure !== null ? (
        <ErrorState failure={site.failure} onRetry={site.retry} />
      ) : null}
      {schedule.state.status === 'error' ? (
        <ErrorState failure={schedule.state.failure} onRetry={schedule.refetch} />
      ) : null}
      {site.status === 'ready' && site.site === null ? (
        <Empty
          title="No site yet"
          body={`Add your website first. Nothing can be scheduled until ${brand.productName} has something to run against.`}
          action={
            <Link className="ar-btn ar-btn--primary ar-btn--sm" to="/seo/sites">
              Go to Sites
            </Link>
          }
        />
      ) : null}

      {site.site !== null && schedule.state.status === 'ready' && schedule.state.data !== null ? (
        <SettingRow
          key={site.site.id}
          label="Schedule"
          summary={summariseSchedule(schedule.state.data)}
          savedAt={savedAt}
          editable={editable}
          editing={one.editing === 'schedule'}
          onEdit={() => {
            one.open('schedule')
          }}
          onCancel={one.close}
        >
          <RunScheduleForm
            schedule={schedule.state.data}
            domain={site.site.domain}
            automationAvailable={tenant.automation_available}
            submitLabel="Save"
            onSubmit={async (next) => {
              const result = await save.run(next)
              if (result.ok) {
                setSavedAt(new Date().toISOString())
                one.close()
                schedule.refetch()
                toast.show({
                  kind: result.data.synced ? 'success' : 'info',
                  title: 'Saved',
                  body: result.data.synced
                    ? describeCadence(result.data, brand.productName)
                    : `Your settings are saved. ${brand.productName} could not reach the scheduler — it will be retried.`,
                })
              }
              return result
            }}
          />
        </SettingRow>
      ) : null}
    </div>
  )
}
